// New session keeps today's form and reviewed adjacent Model/Effort/Mode
// arrangement. Models are admitted runner offers. Each model's discovered
// default effort is selected with it, and can then be changed by the person.
//
// A runner is marked when it is offline, and when it takes no work started
// elsewhere, which the server refuses: that one cannot be chosen, nor an
// agent not signed in on its machine. The last runner, agent, model and effort
// started with are remembered for each project, in this browser. Where none is,
// the dialog opens with the agent's saved default model and effort (Settings,
// Agents), else its reported default, else the latest Opus (Poulpe-497). The
// session's id is the dialog's, so a start sent again after a failure, the
// same, starts one session (sessions/attempt.ts); a refusal says why, and
// the runners are read again, since what they offer may have changed.
//
// A plan's phase opens it filled with a dispatcher's prompt and title instead
// (plans/dispatch.ts, Poulpe-581).
//
// The prompt takes the composer's keys (Poulpe-703): ↵ starts the session,
// ⇧↵ is a new line; ⌘↵ starts it from anywhere in the dialog.
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { flushSync } from 'react-dom'
import { useWorkspaceDraft } from '../workspace/drafts'
import { ApiError, errorMessage } from '../api/client'
import type { Issue, Project, Runner, RunnerAgent, Session } from '../api/types'
import { useMounted } from '../components/layout'
import { Working } from '../components/Working'
import { useAttempt } from '../sessions/attempt'
import { useRunners } from '../sessions/context'
import { useBoardState, useStore } from '../store/context'
import { needsServer } from '../components/needsServer'
import { issueKey } from '../vocab'
import { StateChip } from './bits'
import { activeRunners, agentName, engineOf } from './format'
import { noteTitle } from './titles'
import { FolderChoices, issueWorktrees } from './FolderChoices'
import type { SessionPlace } from '../api/types'
import { ChoiceSelect } from './ChoiceSelect'
import { choices, modelEffort, modeChoices, savedModel, startupEffort, startupMode, startupModels } from './choices'
import { modelVariants, modelVariantKey, retiredCodexModel } from './modelVariants'
import type { ConfigChoice, CyberAccessProgram } from '../api/types'
import './controls.css'
import { DISCARD_QUESTION, useUnsavedChanges } from '../components/unsaved'

export interface NewSessionProps {
  /** The project it starts in, unless he chooses another. */
  projectId: string | null
  /** The issue it was opened for: the dialog is filled from it. */
  issue?: Issue
  /** What it is filled with instead, when opened for something other than an issue (a plan's phase, plans/dispatch.ts). */
  seed?: { prompt: string; title: string }
  onStarted: (session: Session) => void
  onClose: () => void
}

/** What a session started for an issue is first told: the issue, and the repository's own rules of work. */
export function firstPrompt(key: string, title: string | null | undefined): string {
  return `Work on ${key}${title ? `: ${title}` : ''}.\n\nFollow the repository's AGENTS.md. Read the issue on the board first, then plan: present the plan and wait for my approval before you edit anything.`
}

/** What he typed, read as the key of an issue of this project in any spelling (`poulpe-7`, `Poulpe-007`, `7`); null for anything else. */
export function spellKey(typed: string, project: Pick<Project, 'key'>): string | null {
  const escaped = project.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const found = new RegExp(`^(?:${escaped}-)?0*(\\d{1,9})$`, 'i').exec(typed.trim())
  return found ? issueKey(project.key, Number(found[1])) : null
}

/** The last runner, agent, model and effort a session of a project was started with, in this browser. */
interface Picks {
  runner: string | null
  agent: string | null
  model: string | null
  effort: string | null
}

const PICKS_KEY = 'poulpe.code.picks'
const NO_PICKS: Picks = { runner: null, agent: null, model: null, effort: null }
const text = (value: unknown) => (typeof value === 'string' ? value : null)
const textOrNone = (value: unknown): value is string | null => value === null || typeof value === 'string'
const isText = (value: unknown): value is string => typeof value === 'string'
const clean = () => false

export function readPicks(project: string | null): Picks {
  if (!project) return NO_PICKS
  try {
    const held = JSON.parse(window.localStorage.getItem(`${PICKS_KEY}.${project}`) ?? 'null') as Record<string, unknown> | null
    return held ? { runner: text(held.runner), agent: text(held.agent), model: text(held.model), effort: text(held.effort) } : NO_PICKS
  } catch {
    return NO_PICKS
  }
}

function savePicks(project: string, picks: Picks): void {
  try {
    window.localStorage.setItem(`${PICKS_KEY}.${project}`, JSON.stringify(picks))
  } catch {
    // Without storage the dialog starts from the first it can choose.
  }
}

const takesWork = (runner: Runner) => runner.accepts_work_from_elsewhere
const canRun = (agent: RunnerAgent) => agent.signed_in !== false

/** A runner as its option reads: its name, and whether it is offline or driven only from its own machine. */
function runnerLabel(runner: Runner): string {
  return [runner.name, !runner.connected && 'Offline', !takesWork(runner) && 'Driven only from its own machine'].filter(Boolean).join(' · ')
}


function focusable(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>('textarea, input, select, button, a[href]')].filter((el) => !el.hasAttribute('disabled') && el.tabIndex >= 0)
}

export function NewSessionDialog({ projectId, issue: given, seed, onStarted, onClose }: NewSessionProps) {
  const store = useStore()
  const { projects, issues, sessions, offline } = useBoardState()
  const mounted = useMounted()
  const attempt = useAttempt()
  const { runners, loading, error: readError, reload } = useRunners()
  const titleId = useId()
  const dialog = useRef<HTMLFormElement>(null)
  const promptBox = useRef<HTMLTextAreaElement>(null)

  const [chosenProject, setProject] = useWorkspaceDraft<string | null>('session.new.project', given?.project ?? projectId, textOrNone, clean)
  const project = (chosenProject ? projects.get(chosenProject) : undefined) ?? (projects.size === 1 ? [...projects.values()][0] : undefined)
  const [issueText, setIssueText] = useWorkspaceDraft('session.new.issue', given?.key ?? '', isText, clean)
  // What the dialog wrote for him, until he writes over it.
  const [prompt, setPrompt] = useWorkspaceDraft<string | null>('session.new.prompt', null, textOrNone, value => value !== null && value.trim() !== '')
  const [chosenRunner, setRunner] = useWorkspaceDraft<string | null>('session.new.runner', null, textOrNone, clean)
  const [chosenAgent, setAgent] = useWorkspaceDraft<string | null>('session.new.agent', null, textOrNone, clean)
  const [chosenModel, setModel] = useWorkspaceDraft<string | null>('session.new.model', null, textOrNone, clean)
  const [chosenProgram, setProgram] = useState<CyberAccessProgram | undefined>(undefined)
  const [chosenEffort, setEffort] = useState<ConfigChoice | null>(null)
  const [chosenMode, setMode] = useState('')
  const [chosenPlace, setPlace] = useState<SessionPlace>({kind: 'worktree'})
  // Optional: a person's own title, which its agent's later titles leave alone.
  const [titleText, setTitleText] = useState(seed?.title ?? '')
  const [turns, setTurns] = useState('')
  const [minutes, setMinutes] = useState('')
  const budget = {turns: turns === '' ? null : Number(turns), minutes: minutes === '' ? null : Number(minutes)}
  const budgetValid = Object.values(budget).every(n => n === null || (Number.isInteger(n) && n > 0 && n <= 100_000))
  const [starting, setStarting] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const picks = readPicks(project?.id ?? null)
  const listed = activeRunners(runners)
  // What was chosen, while it can be; else what was last started with; else the first that can be.
  const runner =
    listed.find((r) => r.id === chosenRunner && takesWork(r)) ?? listed.find((r) => r.id === picks.runner && takesWork(r)) ?? listed.find(takesWork) ?? null
  const agents = runner?.agents ?? []
  const agent = agents.find((a) => a.agent === chosenAgent && canRun(a)) ?? agents.find((a) => a.agent === picks.agent && canRun(a)) ?? agents.find(canRun) ?? null
  const offered = startupModels(agent).filter(model => agent?.vendor !== 'openai' || !retiredCodexModel(model.id))
  const programs = agent?.vendor === 'openai' ? Object.fromEntries(Object.entries(agent.catalogue?.model_details ?? {}).map(([id, details]) => [id, details.access_programs ?? []])) : {}
  // What was chosen; else what was last started with; else what the agent saves or reports as its default. Each only while it is offered.
  const remembered = agent?.agent === picks.agent ? picks : null
  const model = [chosenModel, remembered?.model, savedModel(agent, offered)].find(id => offered.some(choice => choice.id === id)) ?? ''
  const program = agent?.vendor === 'openai' ? chosenProgram ?? (programs[model]?.includes('standard') ? 'standard' as const : undefined) : undefined
  const variants = modelVariants(offered, programs, model, program)
  const effort = startupEffort(agent, model)
  const efforts = choices(effort?.choices)
  // Likewise: what was chosen; the effort last started with that model; the agent's saved one, which another model does not inherit; else the model's own default.
  const rememberedEffort = effort && remembered?.model === model && remembered.effort ? {id: effort.id, value: remembered.effort} : null
  const saved = agent?.preferences
  const savedEffort = !saved?.model || saved.model === model ? saved?.effort : null
  const effortPicks = [chosenEffort, rememberedEffort, savedEffort].filter((choice): choice is ConfigChoice => !!choice)
  const effortValue = effortPicks.find(choice => choice.id === effort?.id && efforts.some(level => level.id === choice.value)) ?? modelEffort(effort, null)
  const modes = modeChoices(agent?.catalogue?.modes)
  const mode = startupMode(agent, chosenMode)

  // What he typed, read as a key of this project in any spelling and written the one way.
  const typed = issueText.trim()
  const spelled = project && typed ? spellKey(typed, project) : null
  const issueValid = typed === '' || spelled !== null
  const known = spelled ? [...issues.values()].find((i) => i.project === project?.id && i.key === spelled) : undefined
  const existing = issueWorktrees([...sessions.values()], project?.id, known?.id, runner?.id)
  const place: SessionPlace = chosenPlace.kind === 'existing' && !existing.some(s => s.id === chosenPlace.session) ? {kind: 'worktree'} : chosenPlace
  const written = seed?.prompt ?? (given && given.project === project?.id ? firstPrompt(given.key, given.title) : '')
  const promptText = prompt ?? written
  // He wrote something of his own: closing asks before it throws it away, and so does closing its tab or leaving its page.
  const dirty = prompt !== null && prompt.trim() !== ''
  useUnsavedChanges(dirty && !starting)

  let cannot: string | null = null
  if (!project) cannot = 'Choose a project.'
  else if (!budgetValid) cannot = 'Budget turns and minutes are 1–100,000, or empty for no limit.'
  else if (promptText.trim() === '') cannot = 'A session starts with a prompt.'
  else if (!issueValid || (typed !== '' && !known)) cannot = 'Choose an issue in this project.'
  else if (runners !== null && !runner) cannot = 'Choose a runner.'
  else if (runner && !agent) cannot = 'Choose an agent.'
  else if (offered.length > 0 && !model) cannot = 'Choose a model.'
  else if (program && !programs[model]?.includes(program)) cannot = 'Choose an available Codex model.'
  else if (efforts.length > 0 && !effortValue) cannot = 'Choose effort.'

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    const box = promptBox.current
    box?.focus()
    box?.setSelectionRange(box.value.length, box.value.length)
    return () => previous?.focus?.()
  }, [])

  const close = () => {
    if (starting) return
    if (dirty && !window.confirm(DISCARD_QUESTION)) return
    onClose()
  }

  const start = async () => {
    if (store.getState().offline || starting || cannot !== null || !project || !runner || !agent) return
    const before = store.getState().me
    const identity = JSON.stringify([before?.workspace.id, before?.member?.id])
    const sameScope = () => {const state = store.getState();return state.status !== 'signed_out' && JSON.stringify([state.me?.workspace.id, state.me?.member?.id]) === identity}
    const words = promptText.trim()
    const chosenTitle = titleText.trim()
    setStarting(true)
    setProblem(null)
    const { mutationId, newId } = attempt.ids(JSON.stringify([project.id, known?.id ?? null, runner.id, agent.agent, model, program, mode, place, effortValue, words, chosenTitle, budget]))
    try {
      const session = await store.sendSessionOp({
        mutation_id: mutationId,
        op: 'start_session',
        args: {
          session_id: newId,
          project_id: project.id,
          ...(known ? { issue_id: known.id } : {}),
          runner_id: runner.id,
          agent: agent.agent,
          place,
          ...(model ? { model } : {}),
          ...(program ? {cyber_access_program: program} : {}),
          ...(mode && place.kind !== 'checkout' ? {mode} : {}),
          ...(effortValue ? {effort: effortValue} : {}),
          prompt: words,
          ...(turns !== '' || minutes !== '' ? {budget} : {}),
          ...(chosenTitle ? { title: chosenTitle } : {}),
        },
      })
      if (!sameScope()) return
      attempt.applied()
      savePicks(project.id, { runner: runner.id, agent: agent.agent, model: model || null, effort: effortValue?.value ?? null })
      noteTitle(session.id, { prompt: words })
      if (mounted.current) {
        // Commit the cleared draft before the caller's navigation checks it.
        flushSync(() => setPrompt(null))
        onStarted(session)
      }
    } catch (err) {
      if (!mounted.current || !sameScope()) return
      setStarting(false)
      setProblem(errorMessage(err))
      if (err instanceof ApiError && err.status === 409) reload()
    }
  }

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    void start()
  }

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) return
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close()
    } else if (event.key === 'Enter' && (event.metaKey || event.ctrlKey || (event.target === promptBox.current && !event.shiftKey && !event.altKey))) {
      event.preventDefault()
      void start()
    } else if (event.key === 'Tab' && dialog.current) {
      // The dialog keeps the focus: past its last control comes its first.
      const stops = focusable(dialog.current)
      const first = stops[0]
      const last = stops[stops.length - 1]
      if (!first || !last) return
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
  }

  return (
    <div
      className="palette-backdrop code-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) close()
      }}
    >
      <form ref={dialog} className="code-dialog" role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={onSubmit} onKeyDown={onKeyDown} noValidate>
        <h2 className="code-dialog-title" id={titleId}>
          New session
        </h2>
        <div className="code-dialog-body">
          <label className="field">
            <span>Project</span>
            <select
              value={project?.id ?? ''}
              onChange={(event) => {
                setProject(event.target.value)
                setIssueText('')
              }}
            >
              <option value="" disabled>
                Choose a project
              </option>
              {[...projects.values()].map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Title</span>
            <input
              type="text"
              value={titleText}
              onChange={(event) => setTitleText(event.target.value)}
              maxLength={120}
              autoComplete="off"
              placeholder="Optional: else the prompt's first line"
            />
          </label>
          <details>
            <summary>Budget</summary>
            <div className="code-dialog-row">
              <label className="field"><span>Turns</span><input type="number" min="1" max="100000" step="1" value={turns} onChange={event => setTurns(event.target.value)} placeholder="No limit" disabled={starting} /></label>
              <label className="field"><span>Minutes</span><input type="number" min="1" max="100000" step="1" value={minutes} onChange={event => setMinutes(event.target.value)} placeholder="No limit" disabled={starting} /></label>
            </div>
          </details>
          <label className="field">
            <span>Prompt</span>
            <textarea
              ref={promptBox}
              className="textarea code-dialog-prompt"
              value={promptText}
              onChange={(event) => setPrompt(event.target.value)}
              rows={6}
              required
              aria-keyshortcuts="Enter Meta+Enter Control+Enter"
              placeholder="What the session works on"
            />
          </label>

          <div className="code-dialog-row">
            <label className="field code-dialog-issue">
              <span>Issue</span>
              <input
                type="text"
                value={issueText}
                onChange={(event) => setIssueText(event.target.value)}
                onBlur={() => setIssueText(spelled ?? issueText)}
                placeholder={project ? issueKey(project.key, 123) : undefined}
                autoComplete="off"
                spellCheck={false}
                aria-invalid={!issueValid || undefined}
              />
            </label>
          </div>
          {spelled && (
            <p className="code-dialog-issue-line" role="status">
              {known ? (
                <>
                  <StateChip state={known.state} /> <span>{known.title}</span>
                </>
              ) : (
                <span className="muted">The board holds no {spelled}.</span>
              )}
            </p>
          )}

          <FolderChoices value={place} onChange={setPlace} existing={existing} disabled={starting}/>

          {runners === null ? (
            loading ? (
              <p className="code-dialog-note" aria-busy="true">
                <Working />
              </p>
            ) : (
              <p className="code-dialog-note" role="alert">
                Could not read the runners: {readError}{' '}
                <button type="button" className="button button-quiet button-small" onClick={reload}>
                  Try again
                </button>
              </p>
            )
          ) : listed.length === 0 ? (
            <p className="code-dialog-note">No runner is registered in this workspace yet.</p>
          ) : (
            <div className="code-dialog-row code-dialog-picks">
              <label className="field">
                <span>Runner</span>
                <select
                  value={runner?.id ?? ''}
                  onChange={(event) => {
                    setRunner(event.target.value)
                    setAgent(null)
                    setModel('')
                    setProgram(undefined)
                    setEffort(null)
                    setMode('')
                  }}
                >
                  {!runner && <option value="">Choose a runner</option>}
                  {listed.map((r) => (
                    <option key={r.id} value={r.id} disabled={!takesWork(r)}>
                      {runnerLabel(r)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Agent</span>
                <select
                  value={agent?.agent ?? ''}
                  disabled={agents.length === 0}
                  onChange={(event) => {
                    setAgent(event.target.value)
                    setModel('')
                    setProgram(undefined)
                    setEffort(null)
                    setMode('')
                  }}
                >
                  {!agent && <option value="">{agents.length === 0 ? 'None offered' : 'Choose an agent'}</option>}
                  {agents.map((a) => (
                    <option key={a.agent} value={a.agent} disabled={!canRun(a)}>
                      {canRun(a) ? agentName(a.agent) : `${agentName(a.agent)} · Not signed in on this machine`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Model</span>
                <ChoiceSelect label="Model" value={modelVariantKey(model, program, !!program && !programs[model]?.includes(program))} choices={variants} disabled={!agent || offered.length === 0} missing={offered.length === 0 ? 'No models reported' : undefined} onChange={value => {const choice = variants.find(choice => choice.id === value);if (choice) {setModel(choice.modelId);setProgram(choice.selectionProgram);setEffort(modelEffort(startupEffort(agent, choice.modelId), effortValue))}}}/>
              </label>
            </div>
          )}

          {agent && <div className="code-dialog-row code-dialog-picks">
            <label className="field"><span>Effort</span><ChoiceSelect label="Effort" value={effortValue?.value ?? null} choices={efforts} disabled={efforts.length === 0} missing={efforts.length === 0 ? 'Not reported for this model' : undefined} onChange={value => {if (effort) setEffort({id: effort.id, value})}}/></label>
            {place.kind !== 'checkout' && (modes.length > 0 || engineOf(agent.agent) === 'claude') && <label className="field"><span>Mode</span><ChoiceSelect label="Mode" value={mode} choices={modes} disabled={modes.length === 0} missing="Agent's own" onChange={setMode}/></label>}
          </div>}

          {problem && (
            <p className="text-danger code-dialog-problem" role="alert">
              The session was not started: {problem}
            </p>
          )}
        </div>
        <div className="form-actions code-dialog-actions">
          <span className="muted form-hint">{cannot ?? <span className="form-hint-keys">↵ starts it, ⇧↵ adds a line</span>}</span>
          <button type="button" className="button" onClick={close} disabled={starting}>
            Cancel
          </button>
          <button type="submit" className="button button-primary" disabled={offline || starting || cannot !== null || runners === null} {...needsServer(offline)}>
            {starting ? 'Starting…' : 'Start session'}
          </button>
        </div>
      </form>
    </div>
  )
}
